import { redirect } from "react-router";
import { apiClient } from "~/api/api-client";
import { TENANT_ID_HEADER } from "~/constants/tenant";
import { env } from "~/env";
import { getTenantIdFromPathname } from "~/state/tenant";
import { authService } from "./auth-service";
import { CSRF_TOKEN_COOKIE, setCookie } from "./cookie";
import { EMBED_AUTH_WAIT_MS, isEmbedModeSearchParams } from "./embed-utils";

async function getCsrfToken(pathname: string) {
  const tenantId = getTenantIdFromPathname(pathname);
  const options = tenantId
    ? {
        headers: {
          [TENANT_ID_HEADER]: tenantId,
        },
      }
    : undefined;

  const { data, error } = options
    ? await apiClient.GET("/auth/csrf-token", options)
    : await apiClient.GET("/auth/csrf-token");

  const fetchSucceeded = !error && Boolean(data?.csrf_token);
  if (fetchSucceeded) {
    setCookie(CSRF_TOKEN_COOKIE, data.csrf_token);
  }

  if (env.VITE_CSRF_TOKEN_OVERRIDE) {
    setCookie(CSRF_TOKEN_COOKIE, env.VITE_CSRF_TOKEN_OVERRIDE);
    return;
  }

  if (!fetchSucceeded) {
    throw new Error("Failed to fetch CSRF token");
  }
}

// Runs before loaders and before tenant-context-middleware
// Ensures user is authenticated before setting tenant context
export const authMiddleware = async ({ request }: { request: Request }) => {
  await authService.ensureInitialized();

  const url = new URL(request.url);
  if (url.pathname === "/auth/callback") {
    return;
  }
  if (!authService.isAuthenticated()) {
    if (isEmbedModeSearchParams(url.searchParams)) {
      await authService.waitForEmbedToken(EMBED_AUTH_WAIT_MS);
    }
    if (!authService.isAuthenticated()) {
      throw redirect(authService.getLoginUrl() || "/");
    }
  }

  await getCsrfToken(url.pathname);
};
